// src/components/CollectionWatchlistCard.jsx
// "À surveiller dans la collection" (brief Vie de l'œuvre §6).
// Ton volontairement bienveillant : ce sont des points d'attention, pas des alertes
// anxiogènes. Une œuvre peut très bien traîner dans un garde-meuble sans que ce soit
// un drame — on aide juste à améliorer la situation.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Handshake,
  Shield,
  AlertTriangle,
  TrendingDown,
  MapPin,
  FileText,
  Stethoscope,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabaseClient";
import { applyProposalFilter, proposalCollectionIds } from "../lib/proposalExclusion";

const DAYS_30 = 30 * 24 * 60 * 60 * 1000;
const MONTHS_24 = 24 * 30 * 24 * 60 * 60 * 1000;

function inDays(days) {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

function artworkLabel(a) {
  if (!a) return "";
  return [a.title, a.artist].filter(Boolean).join(" — ") || a.inventory_number || "Œuvre";
}

export default function CollectionWatchlistCard({ userId, collectionId }) {
  const { t, i18n } = useTranslation();
  const dateLocale = { fr: "fr-FR", en: "en-GB", de: "de-DE" }[String(i18n?.resolvedLanguage || i18n?.language || "fr").slice(0, 2)] || "fr-FR";
  const { user } = useAuth();
  const ownerId = userId || user?.id || null;
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!ownerId) {
      setLoading(false);
      return;
    }

    (async () => {
      setLoading(true);

      const scope = (q) => (collectionId ? q.eq("collection_id", collectionId) : q);
      // Les œuvres d'une proposition d'acquisition ne sont pas détenues : hors alertes de suivi
      const proposalIds = collectionId ? [] : await proposalCollectionIds(ownerId);
      const scopeArt = (q) => applyProposalFilter(scope(q), proposalIds);

      const [loans, insurances, losses, staleValuations, noLocation, missingDocs, conservationDue] =
        await Promise.all([
          scopeArt(
            supabase
              .from("artworks")
              .select("id,title,artist,inventory_number,loan_end_date")
              .eq("client_id", ownerId)
              .eq("loan_status", "on_loan")
          ),
          scope(
            supabase
              .from("insurance")
              .select("id,artwork_id,expiry_date,insurer,artworks!inner(id,title,artist,inventory_number,client_id)")
              .eq("artworks.client_id", ownerId)
              .not("expiry_date", "is", null)
              .lte("expiry_date", inDays(30))
          ),
          scope(
            supabase
              .from("artwork_losses")
              .select("id,artwork_id,loss_type,loss_date,artworks!inner(id,title,artist,inventory_number,client_id)")
              .eq("artworks.client_id", ownerId)
              .is("recovered_at", null)
          ),
          scopeArt(
            supabase
              .from("artworks")
              .select("id,title,artist,inventory_number,last_estimation_date,current_estimated_value")
              .eq("client_id", ownerId)
              .not("current_estimated_value", "is", null)
              .lt("last_estimation_date", new Date(Date.now() - MONTHS_24).toISOString().slice(0, 10))
          ),
          scopeArt(
            supabase
              .from("artworks")
              .select("id,title,artist,inventory_number")
              .eq("client_id", ownerId)
              .is("location_id", null)
          ),
          scopeArt(
            supabase
              .from("artworks")
              .select("id,title,artist,inventory_number,documents,invoice,certificate_auth")
              .eq("client_id", ownerId)
              .or("documents.is.null,documents.eq.")
              .is("invoice", null)
          ),
          scope(
            supabase
              .from("conservation_reports")
              .select("id,artwork_id,recommendation,risk_level,next_inspection_due,artworks!inner(id,title,artist,inventory_number,client_id)")
              .eq("artworks.client_id", ownerId)
              .not("recommendation", "is", null)
          ),
        ]);

      // Dernier constat d'état par œuvre : plus de 24 mois ou jamais.
      let staleConditionRows = [];
      try {
        const [activeArtworks, checks] = await Promise.all([
          scopeArt(
            supabase
              .from("artworks")
              .select("id,title,artist,inventory_number")
              .eq("client_id", ownerId)
              .eq("artwork_status", "active")
              .limit(5000)
          ),
          scope(
            supabase
              .from("artwork_lifecycle_events")
              .select("artwork_id,event_date_start")
              .eq("client_id", ownerId)
              .eq("event_type", "condition_check")
              .is("deleted_at", null)
              .limit(10000)
          ),
        ]);
        const lastCheck = new Map();
        for (const e of checks.data || []) {
          const ts = new Date(e.event_date_start).getTime();
          if (!Number.isFinite(ts)) continue;
          if (ts > (lastCheck.get(e.artwork_id) || 0)) lastCheck.set(e.artwork_id, ts);
        }
        const limitMs = Date.now() - MONTHS_24;
        staleConditionRows = (activeArtworks.data || [])
          .filter((a) => (lastCheck.get(a.id) || 0) < limitMs)
          .map((a) => {
            const ts = lastCheck.get(a.id);
            return {
              id: a.id,
              label: artworkLabel(a),
              detail: ts
                ? t("watchlist.conditionCheck.lastOn", {
                    date: new Date(ts).toLocaleDateString(dateLocale),
                    defaultValue: `Dernier constat d'état : ${new Date(ts).toLocaleDateString(dateLocale)}`,
                  })
                : t("watchlist.conditionCheck.never", { defaultValue: "Aucun constat d'état enregistré" }),
            };
          });
      } catch {
        staleConditionRows = [];
      }

      if (!alive) return;

      const soonMs = Date.now() + DAYS_30;
      const loanRows = (loans.data || []).map((a) => ({
        id: a.id,
        label: artworkLabel(a),
        detail: a.loan_end_date
          ? new Date(a.loan_end_date).getTime() <= soonMs
            ? t("watchlist.loans.returningSoon", { date: new Date(a.loan_end_date).toLocaleDateString(dateLocale), defaultValue: `Retour prévu le ${new Date(a.loan_end_date).toLocaleDateString(dateLocale)}` })
            : t("watchlist.loans.until", { date: new Date(a.loan_end_date).toLocaleDateString(dateLocale), defaultValue: `Jusqu'au ${new Date(a.loan_end_date).toLocaleDateString(dateLocale)}` })
          : t("watchlist.loans.noEndDate", { defaultValue: "Date de retour non renseignée" }),
      }));

      const insuranceRows = (insurances.data || []).map((r) => ({
        id: r.artwork_id,
        label: artworkLabel(r.artworks),
        detail: `${r.insurer || ""} · ${t("watchlist.insurance.expiresOn", { date: new Date(r.expiry_date).toLocaleDateString(dateLocale), defaultValue: `échéance le ${new Date(r.expiry_date).toLocaleDateString(dateLocale)}` })}`,
      }));

      const lossRows = (losses.data || []).map((r) => ({
        id: r.artwork_id,
        label: artworkLabel(r.artworks),
        detail: `${r.loss_type || ""} · ${r.loss_date ? new Date(r.loss_date).toLocaleDateString(dateLocale) : ""}`,
      }));

      const valuationRows = (staleValuations.data || []).map((a) => ({
        id: a.id,
        label: artworkLabel(a),
        detail: t("watchlist.valuations.lastOn", {
          date: a.last_estimation_date ? new Date(a.last_estimation_date).toLocaleDateString(dateLocale) : "?",
          defaultValue: `Dernière estimation : ${a.last_estimation_date ? new Date(a.last_estimation_date).toLocaleDateString(dateLocale) : "inconnue"}`,
        }),
      }));

      const locationRows = (noLocation.data || []).map((a) => ({
        id: a.id,
        label: artworkLabel(a),
        detail: t("watchlist.location.hint", { defaultValue: "Aucun emplacement renseigné" }),
      }));

      const docRows = (missingDocs.data || []).map((a) => ({
        id: a.id,
        label: artworkLabel(a),
        detail: t("watchlist.documents.hint", { defaultValue: "Facture ou document justificatif manquant" }),
      }));

      const conservationRows = (conservationDue.data || []).map((r) => ({
        id: r.artwork_id,
        label: artworkLabel(r.artworks),
        detail: r.recommendation,
      }));

      const built = [
        {
          key: "loans",
          icon: Handshake,
          color: "#7c3aed",
          title: t("watchlist.loans.title", { defaultValue: "Œuvres actuellement en prêt" }),
          items: loanRows,
        },
        {
          key: "insurance",
          icon: Shield,
          color: "#a16207",
          title: t("watchlist.insurance.title", { defaultValue: "Assurances arrivant à échéance (30 jours)" }),
          items: insuranceRows,
        },
        {
          key: "losses",
          icon: AlertTriangle,
          color: "#b91c1c",
          title: t("watchlist.losses.title", { defaultValue: "Sinistres non clôturés" }),
          items: lossRows,
        },
        {
          key: "valuations",
          icon: TrendingDown,
          color: "#a16207",
          title: t("watchlist.valuations.title", { defaultValue: "Estimations anciennes (+ de 24 mois)" }),
          items: valuationRows,
        },
        {
          key: "conditionCheck",
          icon: Stethoscope,
          color: "#0891b2",
          title: t("watchlist.conditionCheck.title", { defaultValue: "Constat d'état à renouveler (+ de 24 mois)" }),
          items: staleConditionRows,
        },
        {
          key: "location",
          icon: MapPin,
          color: "#1d4ed8",
          title: t("watchlist.location.title", { defaultValue: "Localisation à confirmer" }),
          items: locationRows,
        },
        {
          key: "documents",
          icon: FileText,
          color: "#6b7280",
          title: t("watchlist.documents.title", { defaultValue: "Documents à compléter" }),
          items: docRows,
        },
        {
          key: "conservation",
          icon: Stethoscope,
          color: "#0891b2",
          title: t("watchlist.conservation.title", { defaultValue: "Recommandations de conservation en attente" }),
          items: conservationRows,
        },
      ].filter((s) => s.items.length > 0);

      setSections(built);
      setLoading(false);
    })();

    return () => {
      alive = false;
    };
  }, [ownerId, collectionId, t, dateLocale]);

  if (loading) return null;
  if (sections.length === 0) return null;

  const totalCount = sections.reduce((sum, s) => sum + s.items.length, 0);

  return (
    <section className="mb-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-400">
          {t("watchlist.eyebrow", { defaultValue: "Vie de la collection" })}
        </p>
        <h2 className="mt-1 font-serif text-xl font-semibold text-gray-950" style={{ fontFamily: "Georgia, serif" }}>
          {t("watchlist.title", { defaultValue: "À surveiller dans la collection" })}
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          {t("watchlist.subtitle", {
            count: totalCount,
            defaultValue: `${totalCount} point${totalCount > 1 ? "s" : ""} d'attention — rien d'urgent, juste de quoi garder une longueur d'avance.`,
          })}
        </p>
      </div>

      <div className="divide-y divide-gray-100">
        {sections.map((s) => {
          const Icon = s.icon;
          const isOpen = expanded === s.key;

          return (
            <div key={s.key} className="py-3">
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : s.key)}
                className="flex w-full items-center justify-between gap-3 text-left"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
                    style={{ backgroundColor: `${s.color}1a` }}
                  >
                    <Icon className="h-4 w-4" style={{ color: s.color }} />
                  </span>
                  <span className="truncate text-sm font-medium text-gray-800">{s.title}</span>
                  <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                    {s.items.length}
                  </span>
                </div>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                />
              </button>

              {isOpen && (
                <ul className="mt-3 space-y-2 pl-11">
                  {s.items.map((item, idx) => (
                    <li key={`${item.id}-${idx}`}>
                      <Link
                        to={`/artwork/${item.id}`}
                        className="block rounded-lg border border-gray-100 px-3 py-2 text-sm hover:border-gray-300 hover:bg-gray-50 transition"
                      >
                        <div className="font-medium text-gray-800">{item.label}</div>
                        {item.detail && <div className="text-xs text-gray-500 mt-0.5">{item.detail}</div>}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
